"use client";
import { ChangeEvent, useEffect, useState } from "react";
import {
  HydratedCanvas,
  getMessageFromNetworkError,
  useRenameCanvas,
} from "@zapier/client";

import {
  Button,
  Field,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Text,
  TextInput,
} from "@zapier/design-system";

import { useNotification } from "@zapier/utils";
import { useEmitCanvasRenameModalEvent } from "@zapier/shared/src/events";
import { useAutoSelectInputText } from "@zapier/shared";
import { useTranslations } from "@zapier/i18n";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  canvas: HydratedCanvas | undefined;
  folderId?: string;
}

export function CanvasRenameModal({
  isOpen,
  onClose,
  canvas,
  folderId,
}: Props) {
  const nameInputIdentifier = "rename-canvas-name";
  const [name, setName] = useState(canvas?.name ?? "");
  const { createNotification } = useNotification();
  const { rename, isLoading } = useRenameCanvas({ folderId });
  const { emit } = useEmitCanvasRenameModalEvent();
  const t = useTranslations("canvas-rename-modal");

  useEffect(() => {
    if (!isOpen) return;
    setName(canvas?.name ?? "");
  }, [canvas?.name, isOpen]);

  useAutoSelectInputText({
    isOpen,
    inputSelector: `#${nameInputIdentifier}`,
    value: canvas?.name,
  });

  const handleCancel = () => {
    emit("cancel");
    onClose();
  };

  const handleSubmit = () => {
    if (!canvas) {
      return;
    }

    const trimmedName = name.trim();

    if (!trimmedName || trimmedName === canvas.name) {
      // Should not be possible due to disabled button, but just in case
      handleCancel();
      return;
    }

    rename(
      { id: canvas.id, name: trimmedName },
      {
        onSuccess: () => {
          createNotification({
            message: (
              <Text>
                {t("notifications.rename-success", {
                  oldName: canvas.name ?? "",
                  newName: trimmedName,
                })}
              </Text>
            ),
            type: "success",
          });
        },
        onError: (error) => {
          createNotification({
            title: t("notifications.rename-error", {
              canvasName: canvas.name ?? "",
            }),
            message: getMessageFromNetworkError(error),
            type: "error",
          });
        },
      },
    );

    emit("confirm");
    onClose();
  };

  if (!isOpen || !canvas) {
    return;
  }

  const trimmedName = name.trim();
  const isSubmitDisabled =
    !trimmedName || trimmedName === canvas.name || isLoading;

  return (
    <ModalDialogRoot
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleCancel();
        }
      }}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          handleSubmit();
        }}
      >
        <ModalDialogHeader textAlign="start">
          {t("header.rename-title", { canvasName: canvas.name ?? "" })}
        </ModalDialogHeader>

        <ModalDialogBody textAlign="start">
          <Field
            label={t("form.name-label")}
            inputId={nameInputIdentifier}
            isRequired
            renderInput={(inputProps) => (
              <TextInput
                {...inputProps}
                // eslint-disable-next-line jsx-a11y/no-autofocus -- intentionally focus the input when the modal opens
                autoFocus
                id={nameInputIdentifier}
                value={name}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setName(event.target.value)
                }
                placeholder={t("form.name-placeholder")}
                isFullWidth
              />
            )}
          />
        </ModalDialogBody>

        <ModalDialogFooter>
          <Button
            variant="secondary"
            type="button"
            onPress={handleCancel}
            isDisabled={isLoading}
          >
            {t("buttons.cancel")}
          </Button>
          <Button type="submit" isDisabled={isSubmitDisabled}>
            {t("buttons.save")}
          </Button>
        </ModalDialogFooter>
      </form>
    </ModalDialogRoot>
  );
}
